import { useState, useEffect, useCallback } from 'react';
import { 
  getPendingConfirmations, 
  confirmAppointment, 
  cancelAppointment,
  markAppointmentMessageSent,
  updateAppointment as updateAppointmentAPI 
} from '../firebase/appointments';

/**
 * Hook personalizado para gerenciar confirmações de agendamentos
 * 
 * @param {string} salonId - ID do salão
 * @param {number} daysAhead - Número de dias à frente para buscar confirmações (padrão: 2)
 * @returns {Object} - Objeto com dados e funções relacionadas a confirmações
 */
export const useConfirmations = (salonId, daysAhead = 3) => {
  const [pendingConfirmations, setPendingConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [messageStatus, setMessageStatus] = useState({});

  /**
   * Carrega confirmações pendentes do Firebase
   */
  const loadConfirmations = useCallback(async () => {
    if (!salonId) return;
    
    try {
      console.log(`[useConfirmations] Iniciando carregamento de confirmações para salonId: ${salonId}, daysAhead: ${daysAhead}`);
      setLoading(true);
      setError(null);
      
      // Aumentando daysAhead para incluir o dia 17 de março nos testes
      const confirmations = await getPendingConfirmations(salonId, daysAhead);
      console.log(`[useConfirmations] Confirmações recebidas: ${confirmations.length}`, confirmations);
      
      // Log para verificar agendamentos de 17/03/2025
      const data17Marco = confirmations.filter(app => app.data === "2025-03-17");
      console.log(`[useConfirmations] Total de agendamentos para 17/03/2025: ${data17Marco.length}`);
      
      if (data17Marco.length > 0) {
        console.log('[useConfirmations] Encontrados agendamentos para 17/03/2025:', data17Marco);
      }
      
      // Inicializar o estado de mensagens enviadas
      const initialMessageStatus = {};
      confirmations.forEach(appointment => {
        initialMessageStatus[appointment.id] = appointment.mensagemEnviada || false;
      });
      
      setMessageStatus(initialMessageStatus);
      setPendingConfirmations(confirmations);
    } catch (err) {
      console.error('[useConfirmations] Erro ao carregar confirmações pendentes:', err);
      setError(err.message || 'Erro ao carregar confirmações pendentes');
    } finally {
      setLoading(false);
    }
  }, [salonId, daysAhead]);

  /**
   * Confirma um agendamento
   * @param {string} appointmentId - ID do agendamento 
   * @returns {Promise<boolean>} - True se confirmado com sucesso
   */
  const handleConfirmAppointment = async (appointmentId) => {
    if (!salonId) return false;
    
    try {
      setLoading(true);
      setError(null);
      
      await confirmAppointment(salonId, appointmentId);
      
      // Atualizar a lista localmente removendo o agendamento confirmado
      setPendingConfirmations(prev => 
        prev.filter(app => app.id !== appointmentId)
      );
      
      return true;
    } catch (err) {
      console.error('Erro ao confirmar agendamento:', err);
      setError(err.message || 'Erro ao confirmar agendamento');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Envia mensagem WhatsApp e marca como enviada
   * @param {Object} appointment - Dados do agendamento
   * @param {string} customMessage - Mensagem personalizada opcional
   * @returns {Promise<boolean>} - True se enviada com sucesso
   */
  const handleSendWhatsAppMessage = async (appointment, customMessage = '') => {
    if (!salonId || !appointment || !appointment.id) return false;
    
    try {
      setLoading(true);
      setError(null);
      
      let message = customMessage;
      
      // Se não foi fornecida uma mensagem personalizada, gerar uma padrão
      if (!message) {
        // Gerar texto da mensagem
        const servicosTexto = Array.isArray(appointment.servicos)
          ? appointment.servicos
              .map(serv => typeof serv === 'object' ? serv.nome : 'Serviço')
              .join(', ')
          : 'seu serviço';
          
        // Formatar a data
        const dataFormatada = appointment.data instanceof Date
          ? appointment.data.toLocaleDateString('pt-BR')
          : typeof appointment.data === 'string'
            ? new Date(appointment.data).toLocaleDateString('pt-BR')
            : 'data agendada';
        
        // Garantir que o horário existe e está formatado
        const horario = appointment.horario 
          ? appointment.horario.substring(0, 5) 
          : 'horário agendado';
        
        // Criar mensagem padrão
        message = `Olá ${appointment.cliente}, seu agendamento para ${servicosTexto} no dia ${dataFormatada} às ${horario} está confirmado! Agradecemos a preferência.`;
      }
      
      // Limpar número de telefone
      const whatsappNumber = appointment.whatsapp?.replace(/\D/g, '').slice(0, 11) || '';
      
      if (!whatsappNumber) {
        throw new Error('Número de WhatsApp não encontrado');
      }
      
      // Abrir WhatsApp Web
      const whatsappUrl = `https://wa.me/55${whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      // Marcar mensagem como enviada no Firebase
      const success = await markAppointmentMessageSent(salonId, appointment.id);
      
      if (success) {
        // Atualizar localmente o status da mensagem enviada
        setMessageStatus(prev => ({
          ...prev,
          [appointment.id]: true
        }));
        
        // Atualizar a lista local de agendamentos para refletir o novo status
        setPendingConfirmations(prev => 
          prev.map(app => 
            app.id === appointment.id 
              ? { ...app, mensagemEnviada: true } 
              : app
          )
        );
      }
      
      return success;
    } catch (err) {
      console.error('Erro ao enviar mensagem WhatsApp:', err);
      setError(err.message || 'Erro ao enviar mensagem WhatsApp');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verifica se um agendamento já teve mensagem enviada
   * @param {string} appointmentId - ID do agendamento
   * @returns {boolean} - True se a mensagem já foi enviada
   */
  const isMessageSent = useCallback((appointmentId) => {
    // Verificar tanto nos dados locais quanto no estado
    const appointment = pendingConfirmations.find(app => app.id === appointmentId);
    return messageStatus[appointmentId] || (appointment && appointment.mensagemEnviada) || false;
  }, [pendingConfirmations, messageStatus]);

  /**
   * Atualiza manualmente a lista de confirmações pendentes
   */
  const refreshConfirmations = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  /**
   * Cancelar um agendamento
   * @param {string} appointmentId - ID do agendamento
   * @returns {Promise<boolean>} - True se cancelado com sucesso
   */
  const handleCancelAppointment = async (appointmentId) => {
    if (!salonId) return false;
    
    try {
      setLoading(true);
      setError(null);
      
      await cancelAppointment(salonId, appointmentId);
      
      // Atualizar a lista localmente com o agendamento cancelado
      setPendingConfirmations(prev => 
        prev.map(app => 
          app.id === appointmentId 
            ? { ...app, cancelado: true, confirmado: false } 
            : app
        )
      );
      
      return true;
    } catch (err) {
      console.error('Erro ao cancelar agendamento:', err);
      setError(err.message || 'Erro ao cancelar agendamento');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Atualizar dados de um agendamento
   * @param {string} appointmentId - ID do agendamento
   * @param {Object} updateData - Dados a serem atualizados
   * @returns {Promise<boolean>} - True se atualizado com sucesso
   */
  const handleUpdateAppointment = async (appointmentId, updateData) => {
    if (!salonId) return false;
    
    try {
      setLoading(true);
      setError(null);
      
      await updateAppointmentAPI(salonId, appointmentId, updateData);
      
      // Atualizar a lista local de agendamentos para refletir as alterações
      setPendingConfirmations(prev => 
        prev.map(app => 
          app.id === appointmentId 
            ? { ...app, ...updateData, updatedAt: new Date() } 
            : app
        )
      );
      
      return true;
    } catch (err) {
      console.error('Erro ao atualizar agendamento:', err);
      setError(err.message || 'Erro ao atualizar agendamento');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Efeito para carregar confirmações pendentes quando necessário
  useEffect(() => {
    if (salonId) {
      loadConfirmations();
    }
  }, [salonId, loadConfirmations, refreshTrigger]);

  return {
    pendingConfirmations,
    messageStatus,
    loading,
    error,
    confirmAppointment: handleConfirmAppointment,
    cancelAppointment: handleCancelAppointment,
    updateAppointment: handleUpdateAppointment,
    sendWhatsAppMessage: handleSendWhatsAppMessage,
    isMessageSent,
    refreshConfirmations
  };
}; 